import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  acceptTerms,
  getCurrentTerms,
  type PlatformTermsKind,
  type PlatformTermsView,
} from "../../api/General/PlatformTerms";

export interface TermsReader {
  loading: boolean;
  errorMsg: string | null;
  data: PlatformTermsView | null;
  /** scrolled to bottom OR explicitly clicked "I've read it" */
  reviewed: boolean;
  submitting: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
  markRead: () => void;
  retry: () => void;
  /** Posts /accept when autoAccept, otherwise just signals onAccepted. */
  accept: () => Promise<void>;
}

interface Options {
  autoAccept?: boolean;
  onAccepted?: () => void;
  /** When false, skip fetching (e.g. the hosting dialog is closed). */
  enabled?: boolean;
}

/**
 * useTermsReader — owns the data + read-gate + acceptance state for a single
 * platform-terms kind. Lifted out of TermsView so the scrollable body and a
 * docked footer CTA can share one instance: the body reports scroll progress,
 * the footer button reads `reviewed` / `submitting` and calls `accept`.
 */
export function useTermsReader(
  kind: PlatformTermsKind,
  { autoAccept = false, onAccepted, enabled = true }: Options = {},
): TermsReader {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [data, setData] = useState<PlatformTermsView | null>(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [readClicked, setReadClicked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);

  const fetchTerms = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await getCurrentTerms(kind);
      if (res.errors && res.errors.length > 0) {
        setErrorMsg(res.errors[0]?.message || t("Failed to load terms"));
      } else if (!res.payload) {
        setErrorMsg(t("No published version of these terms yet."));
      } else {
        setData(res.payload);
      }
    } catch {
      setErrorMsg(t("Failed to load terms"));
    } finally {
      setLoading(false);
    }
  }, [kind, t]);

  // Reset gate + content whenever the kind changes (mirrors the old per-kind remount).
  useEffect(() => {
    setData(null);
    setScrolledToBottom(false);
    setReadClicked(false);
  }, [kind]);

  useEffect(() => {
    if (!enabled) return;
    fetchTerms();
  }, [enabled, fetchTerms]);

  // Reset gate when a new document version loads.
  useEffect(() => {
    setScrolledToBottom(false);
    setReadClicked(false);
  }, [data?.id]);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight;
    // Within 24px of the bottom counts as "read"
    if (near < 24) setScrolledToBottom(true);
  }, []);

  // If the content fits without scrolling, the gate is satisfied automatically.
  useEffect(() => {
    if (!data || loading) return;
    const el = scrollRef.current;
    if (!el) return;
    const id = window.setTimeout(() => {
      if (el.scrollHeight - el.clientHeight < 24) setScrolledToBottom(true);
    }, 50);
    return () => window.clearTimeout(id);
  }, [data, loading]);

  const accept = useCallback(async () => {
    if (!data) return;
    if (!autoAccept) {
      onAccepted?.();
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const res = await acceptTerms(data.id);
      if (res.errors && res.errors.length > 0) {
        setErrorMsg(res.errors[0]?.message || t("Failed to record acceptance"));
        setSubmitting(false);
        return;
      }
      onAccepted?.();
    } catch {
      setErrorMsg(t("Failed to record acceptance"));
      setSubmitting(false);
    }
  }, [data, autoAccept, onAccepted, t]);

  return {
    loading,
    errorMsg,
    data,
    reviewed: scrolledToBottom || readClicked,
    submitting,
    scrollRef,
    onScroll,
    markRead: () => setReadClicked(true),
    retry: fetchTerms,
    accept,
  };
}
